import { useCallback, useEffect, useRef, useState } from "react";
import { Ruler, X } from "@phosphor-icons/react";

const MIN = 6; // smallest piece, inches
const inch = (v) => `${Number(v).toFixed(1)}"`;

// Measure / resize tool over the slab photo. All maths is in inches on the slab's
// nominal length × width; the photo is taken to frame the slab edge to edge.
export const SlabMeasure = ({
  photo,
  lengthIn,
  widthIn,
  polygon = [],
  title,
  onClose,
  onInquire,
  onQuote,
  onSave,
}) => {
  const L = Math.max(MIN, Number(lengthIn) || 0);
  const Wd = Math.max(MIN, Number(widthIn) || 0);
  const trimX = Math.min(2, L * 0.05);
  const trimY = Math.min(2, Wd * 0.05);
  const autoRect = useCallback(
    () => ({ x: trimX, y: trimY, w: L - 2 * trimX, h: Wd - 2 * trimY }),
    [L, Wd, trimX, trimY],
  );

  const [mode, setMode] = useState("auto");
  const [rect, setRect] = useState(autoRect);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const boardRef = useRef(null);

  const measure = useCallback(() => {
    const el = boardRef.current;
    if (el) setBox({ w: el.clientWidth, h: el.clientHeight });
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const sx = box.w / L;
  const sy = box.h / Wd;
  const area = (rect.w * rect.h) / 144;

  const clamp = (r) => {
    const w = Math.max(MIN, Math.min(r.w, L));
    const h = Math.max(MIN, Math.min(r.h, Wd));
    return {
      w,
      h,
      x: Math.max(0, Math.min(r.x, L - w)),
      y: Math.max(0, Math.min(r.y, Wd - h)),
    };
  };

  // one drag = one handle; dx/dy are in inches
  const apply = (kind, r0, dx, dy) => {
    let { x, y, w, h } = r0;
    if (kind === "move") return clamp({ x: x + dx, y: y + dy, w, h });
    if (kind.includes("w")) {
      x = Math.min(Math.max(0, r0.x + dx), r0.x + r0.w - MIN);
      w = r0.x + r0.w - x;
    }
    if (kind.includes("e")) w = Math.max(MIN, Math.min(L - r0.x, r0.w + dx));
    if (kind.includes("n")) {
      y = Math.min(Math.max(0, r0.y + dy), r0.y + r0.h - MIN);
      h = r0.y + r0.h - y;
    }
    if (kind.includes("s")) h = Math.max(MIN, Math.min(Wd - r0.y, r0.h + dy));
    return { x, y, w, h };
  };

  const start = (kind) => (e) => {
    if (mode !== "edit" || !sx || !sy) return;
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const y0 = e.clientY;
    const r0 = rect;
    const move = (ev) =>
      setRect(apply(kind, r0, (ev.clientX - x0) / sx, (ev.clientY - y0) / sy));
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const setSize = (k) => (e) => {
    const v = Number(e.target.value);
    if (!Number.isFinite(v)) return;
    setRect((r) => clamp({ ...r, [k]: v }));
  };

  const px = {
    left: rect.x * sx,
    top: rect.y * sy,
    width: rect.w * sx,
    height: rect.h * sy,
  };
  // traced remnant shape (normalised 0–1 over the photo) → pixels, plus a label per side
  const shape =
    polygon.length >= 3 && polygon.every((p) => p.x <= 1.01 && p.y <= 1.01)
      ? polygon.map((p) => ({ x: p.x * box.w, y: p.y * box.h }))
      : [];
  const outline = shape.map((p) => `${p.x},${p.y}`).join(" ");
  const centroid = shape.length
    ? {
        x: shape.reduce((s, p) => s + p.x, 0) / shape.length,
        y: shape.reduce((s, p) => s + p.y, 0) / shape.length,
      }
    : null;
  const sides = shape.map((a, i) => {
    const b = shape[(i + 1) % shape.length];
    const p = polygon[i];
    const q = polygon[(i + 1) % polygon.length];
    const len = Math.hypot((q.x - p.x) * L, (q.y - p.y) * Wd);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const d = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    // push the label just outside the edge, away from the shape's centre
    let nx = -(b.y - a.y) / d;
    let ny = (b.x - a.x) / d;
    if ((mx - centroid.x) * nx + (my - centroid.y) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    return { len, x: mx + nx * 18, y: my + ny * 18 };
  });
  const handles = mode === "edit";
  // a traced remnant shows only its own shape until the visitor asks to size a cut
  const showRect = box.w > 0 && (!outline || handles);
  const shapeArea = shape.length
    ? Math.abs(
        polygon.reduce((s, p, i) => {
          const q = polygon[(i + 1) % polygon.length];
          return s + p.x * L * (q.y * Wd) - q.x * L * (p.y * Wd);
        }, 0),
      ) /
      2 /
      144
    : 0;
  const shownArea = outline && !handles ? shapeArea : area;
  const sizeText =
    outline && !handles
      ? `whole remnant ${inch(L)} × ${inch(Wd)} (${shapeArea.toFixed(1)} ft²)`
      : `${inch(rect.w)} × ${inch(rect.h)} (${area.toFixed(1)} ft²)`;
  const pieceOut = () =>
    outline && !handles
      ? { length: Number(L.toFixed(1)), width: Number(Wd.toFixed(1)), area: Number(shapeArea.toFixed(1)) }
      : { length: Number(rect.w.toFixed(1)), width: Number(rect.h.toFixed(1)), area: Number(area.toFixed(1)) };
  const corner = "absolute h-3.5 w-3.5  bg-[#f28c28] shadow";
  const edge = "absolute bg-[#f28c28]/90  shadow";
  const label =
    "pointer-events-none absolute  bg-[#1c1b19] px-2.5 py-1 text-sm font-bold text-white";

  return (
    <div
      className="site site-overlay fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-8"
      data-testid="slab-measure"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Measure a piece"
    >
      <div
        className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden  bg-white"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="relative flex flex-wrap items-center justify-center gap-3 bg-[#1c1b19] px-5 py-4 text-white sm:gap-5">
          <span className="mr-auto hidden items-center gap-2 text-sm font-medium sm:flex">
            <Ruler size={16} weight="bold" /> {title}
          </span>
          {["auto", "edit"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`measure-mode-${m}`}
              onClick={() => {
                setMode(m);
                if (m === "auto") setRect(autoRect());
              }}
              className={`h-9 w-28  text-sm font-semibold capitalize transition-colors duration-200 ${
                mode === m
                  ? "bg-white text-[#1c1b19]"
                  : "bg-[#4a4844] text-white hover:bg-[#5c5a55]"
              }`}
            >
              {m}
            </button>
          ))}
          <span className="text-xs text-white/70 sm:ml-4">
            {outline && !handles ? "Remnant area:" : "Area:"}{" "}
            <b className="text-base text-[#e0a13d]" data-testid="measure-area">
              {shownArea.toFixed(1)} ft²
            </b>
          </span>
          <button
            type="button"
            data-testid="measure-close"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto flex h-9 w-9 items-center justify-center  text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:absolute sm:right-4 sm:ml-0"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center overflow-auto bg-[#f7f5f1] p-6 sm:p-12">
          <div
            className="relative inline-block select-none touch-none"
            style={{ padding: 34 }}
          >
            <div ref={boardRef} className="relative inline-block">
              {photo ? (
                <img
                  src={photo}
                  alt={title}
                  draggable={false}
                  onLoad={measure}
                  data-testid="measure-photo"
                  className="block max-h-[62vh] max-w-[min(88vw,1000px)] "
                />
              ) : (
                <div
                  className=" bg-[#d9d4cc]"
                  style={{
                    width: "min(88vw, 900px)",
                    aspectRatio: `${L} / ${Wd}`,
                  }}
                />
              )}
              {outline && (
                <>
                  <svg
                    className="pointer-events-none absolute inset-0 h-full w-full"
                    viewBox={`0 0 ${box.w} ${box.h}`}
                    data-testid="measure-shape"
                  >
                    <defs>
                      <mask id="measure-shape-mask">
                        <rect width="100%" height="100%" fill="#fff" />
                        <polygon points={outline} fill="#000" />
                      </mask>
                    </defs>
                    {/* dim what was cut away so the real remnant reads at a glance */}
                    <rect
                      width="100%"
                      height="100%"
                      fill="rgba(28,27,25,0.42)"
                      mask="url(#measure-shape-mask)"
                    />
                    <polygon
                      points={outline}
                      fill="none"
                      stroke="#1c1b19"
                      strokeOpacity="0.9"
                      strokeWidth="7"
                      strokeLinejoin="round"
                    />
                    <polygon
                      points={outline}
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      strokeDasharray="12 8"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {sides.map((sd, i) =>
                    sd.len < 2 ? null : (
                      <span
                        key={i}
                        data-testid={`measure-shape-side-${i}`}
                        style={{ left: sd.x, top: sd.y }}
                        className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap  border border-white/80 bg-[#1c1b19] px-2 py-0.5 text-xs font-bold text-white shadow"
                      >
                        {inch(sd.len)}
                      </span>
                    ),
                  )}
                </>
              )}
              {showRect && (
                <div
                  data-testid="measure-rect"
                  onPointerDown={start("move")}
                  style={px}
                  className={`absolute border-2 border-[#2f7bf5] ${
                    handles ? "cursor-move" : ""
                  }`}
                >
                  <span
                    className={`${label} left-1/2 -translate-x-1/2 -translate-y-1/2`}
                    data-testid="measure-length"
                  >
                    {inch(rect.w)}
                  </span>
                  <span
                    className={`${label} bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2`}
                  >
                    {inch(rect.w)}
                  </span>
                  <span
                    className={`${label} left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90`}
                    data-testid="measure-width"
                  >
                    {inch(rect.h)}
                  </span>
                  <span
                    className={`${label} right-0 top-1/2 -translate-y-1/2 translate-x-1/2 -rotate-90`}
                  >
                    {inch(rect.h)}
                  </span>
                  {handles && (
                    <>
                      <span
                        onPointerDown={start("nw")}
                        data-testid="measure-handle-nw"
                        className={`${corner} -left-2 -top-2 cursor-nwse-resize`}
                      />
                      <span
                        onPointerDown={start("ne")}
                        data-testid="measure-handle-ne"
                        className={`${corner} -right-2 -top-2 cursor-nesw-resize`}
                      />
                      <span
                        onPointerDown={start("sw")}
                        className={`${corner} -bottom-2 -left-2 cursor-nesw-resize`}
                      />
                      <span
                        onPointerDown={start("se")}
                        data-testid="measure-handle-se"
                        className={`${corner} -bottom-2 -right-2 cursor-nwse-resize`}
                      />
                      <span
                        onPointerDown={start("n")}
                        className={`${edge} -top-1 left-1/2 h-2 w-8 -translate-x-1/2 cursor-ns-resize`}
                        style={{ marginLeft: 40 }}
                      />
                      <span
                        onPointerDown={start("s")}
                        className={`${edge} -bottom-1 left-1/2 h-2 w-8 -translate-x-1/2 cursor-ns-resize`}
                        style={{ marginLeft: 40 }}
                      />
                      <span
                        onPointerDown={start("w")}
                        className={`${edge} -left-1 top-1/2 h-8 w-2 -translate-y-1/2 cursor-ew-resize`}
                        style={{ marginTop: 34 }}
                      />
                      <span
                        onPointerDown={start("e")}
                        className={`${edge} -right-1 top-1/2 h-8 w-2 -translate-y-1/2 cursor-ew-resize`}
                        style={{ marginTop: 34 }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-[color:var(--s-line)] px-5 py-4 sm:px-6">
          <div className="text-sm text-[color:var(--s-mute)]">
            {outline ? "Remnant" : "Slab"} {inch(L)} × {inch(Wd)} ·{" "}
            {mode === "edit"
              ? "drag the blue corners, or type a size"
              : outline
                ? "switch to Edit to size a cut piece"
                : 'Auto trims 2" all round — switch to Edit to size your piece'}
            {outline && (
              <span className="block" data-testid="measure-shape-legend">
                <span className="mr-1.5 inline-block h-0 w-5 -translate-y-0.5 border-t-2 border-dashed border-[#1c1b19] align-middle" />
                Dashed outline = the actual remnant, each side in inches
                {handles ? " · blue box = your cut" : ""}
              </span>
            )}
          </div>
          {mode === "edit" && (
            <div
              className="flex items-center gap-2 text-sm"
              data-testid="measure-inputs"
            >
              <label className="flex items-center gap-1.5">
                L
                <input
                  type="number"
                  step="0.5"
                  min={MIN}
                  max={L}
                  value={Number(rect.w.toFixed(1))}
                  onChange={setSize("w")}
                  data-testid="measure-input-length"
                  className="s-round h-10 w-24 !rounded-xl !border !border-[#e6e1d8] px-3 text-sm"
                />
              </label>
              <label className="flex items-center gap-1.5">
                W
                <input
                  type="number"
                  step="0.5"
                  min={MIN}
                  max={Wd}
                  value={Number(rect.h.toFixed(1))}
                  onChange={setSize("h")}
                  data-testid="measure-input-width"
                  className="s-round h-10 w-24 !rounded-xl !border !border-[#e6e1d8] px-3 text-sm"
                />
              </label>
            </div>
          )}
          <button
            type="button"
            data-testid="measure-inquire"
            onClick={() => onInquire(sizeText)}
            className="s-ghost ml-auto"
          >
            {outline && !handles ? "Ask about this remnant" : "Ask about this size"}
          </button>
          {onSave && (
            <button
              type="button"
              data-testid="measure-save"
              onClick={() => onSave(pieceOut())}
              className="s-ghost"
            >
              Save my cut
            </button>
          )}
          {onQuote && (
            <button
              type="button"
              data-testid="measure-quote"
              onClick={() => onQuote(pieceOut())}
              className="s-btn"
            >
              {outline && !handles
                ? "Request a quote for this remnant"
                : "Request a quote for this piece"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlabMeasure;
